import { isValid, parseISO } from 'date-fns';

import {
  cargoTypesService,
  carriersService,
  customersService,
  equipmentTypesService,
  pricingCategoriesService,
  pricingTypesService,
  supplierDiversitiesService,
  tariffTypesService,
  transportationModesService,
} from '@/modules/entities';

import type { Contract, ContractApi } from '../../models';
import { resolveEntity } from '../normalize';

const TIMEZONE_PATTERN = /(Z|[+-]\d{2}:?\d{2})$/;

// TODO: remove this function when backend with Z in the end
const toIsoTimestamp = (date: string | null | undefined): string | undefined => {
  if (!date) {
    return undefined;
  }

  const parsedDate = parseISO(TIMEZONE_PATTERN.test(date) ? date : `${date}Z`);

  return isValid(parsedDate) ? parsedDate.toISOString() : undefined;
};

export const normalizeContract = async (contract: ContractApi): Promise<Contract> => {
  const [
    carrier,
    cargoType,
    customer,
    pricingBasis,
    pricingCategory,
    pricingType,
    supplierDiversity,
    trailer,
    transportationMode,
  ] = await Promise.all([
    resolveEntity(contract.carrierKey, carriersService.find),
    resolveEntity(contract.cargoType, cargoTypesService.find),
    resolveEntity(contract.customerId, customersService.find),
    resolveEntity(contract.pricingBasis, tariffTypesService.find),
    resolveEntity(contract.pricingCategory, pricingCategoriesService.find),
    resolveEntity(contract.pricingType, pricingTypesService.find),
    resolveEntity(contract.supplierDiversity, supplierDiversitiesService.find),
    resolveEntity(contract.trailerKey, equipmentTypesService.find),
    resolveEntity(contract.transportationMode, transportationModesService.find),
  ]);

  return {
    approvedBy: contract.approvedBy ?? null,
    approvedOn: contract.approvedOn ?? null,
    cargoType,
    carrier,
    createdBy: contract.createdBy ?? null,
    createdOn: contract.createdOn ?? null,
    customer,
    customerLaneId: contract.customerLaneId
      ? { id: contract.customerLaneId, label: String(contract.customerLaneId) }
      : null,
    deletedBy: contract.deletedBy ?? null,
    deletedOn: contract.deletedOn ?? null,
    effectiveFrom: toIsoTimestamp(contract.effectiveFrom),
    effectiveTo: toIsoTimestamp(contract.effectiveTo),
    key: contract.key,
    modifiedBy: contract.modifiedBy ?? null,
    modifiedOn: contract.modifiedOn ?? null,
    name: contract.name ?? '',
    pricingBasis,
    pricingCategory,
    pricingType,
    primaryDocumentKey: contract.primaryDocumentKey ?? null,
    serviceLayer: contract.serviceLayer ? { id: contract.serviceLayer, label: contract.serviceLayer } : null,
    status: contract.status ?? null,
    supplierDiversity,
    trailer,
    transportationMode,
    uniqueName: contract.uniqueName ?? '',
  };
};
